// FLAG:docuAiEnhancements — history arithmetic for the receipt chat.
//
// Why a separate module: `rag-chat-section.tsx` pulls in MUI, Redux, and ampersand-app, while its
// only genuinely error-prone part is trimming the history for a retry — pure index arithmetic.
// It belongs where it can be tested without rendering.

/** Structurally compatible with `ChatMessage` in `rag-chat-section.tsx`; deliberately redeclared
 *  here so this module does not need to import anything from the component. */
export interface RetryableMessage {
  role: 'user' | 'assistant'
  content: string
  isError?: boolean
}

/** Index of the last user question BEFORE `index`, or -1. */
export const findPrecedingQuestionIndex = (messages: RetryableMessage[], index: number): number => {
  for (let i = index - 1; i >= 0; i--) {
    if (messages[i].role === 'user') return i
  }
  return -1
}

export const findPrecedingQuestion = (messages: RetryableMessage[], index: number): string | undefined => {
  const questionIndex = findPrecedingQuestionIndex(messages, index)
  return questionIndex >= 0 ? messages[questionIndex].content : undefined
}

export interface RetryPlan {
  /** The question to retry — exactly the user's wording, never a paraphrase. */
  question: string
  /** The state BEFORE this question. The caller appends the question again, which removes the error
   *  bubble and leaves the question exactly once in the history. */
  priorMessages: RetryableMessage[]
}

/**
 * Plans a retry of the last turn — or returns
 * `null` when there is nothing to retry.
 *
 * Deliberately ONLY the last message: retrying in
 * the middle of the history appends the new answer
 * at the end and disrupts the order. And deliberately only for an ERROR bubble: regenerating a
 * successful answer costs another model call even though nothing was broken.
 */
export const planRetry = (messages: RetryableMessage[]): RetryPlan | null => {
  const lastIndex = messages.length - 1
  if (lastIndex < 0 || !messages[lastIndex].isError) return null

  const questionIndex = findPrecedingQuestionIndex(messages, lastIndex)
  if (questionIndex < 0) return null

  return {
    question: messages[questionIndex].content,
    priorMessages: messages.slice(0, questionIndex),
  }
}

export default planRetry
